import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import ZoneDropdown from '../../../components/ZonesDropdownByRegion';
import RegionsDropdown from '../../../components/RegionsDropdown';
import { useAuth } from '../../../hooks/useAuth';
import {
	ApplicationsFilter,
	CandidateScreeningProgressSummaryFilter,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { UndoOutlined } from '@ant-design/icons';
import { useApplicationsList } from '../ApplicationsList/useApplicationsList';
import {
	ApplicationStatus,
	VALID_ABHYASI_ID_REGEX,
} from '../../../shared/Constants';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	open: boolean;
	onClose: () => void;
	filterValue: CandidateScreeningProgressSummaryFilter;
	onApplicationFilterChange: (
		value: CandidateScreeningProgressSummaryFilter
	) => void;
}

export interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

export const FilterDashboard = ({
	open,
	onClose,
	filterValue,
	onApplicationFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' ||
		i18n.language === 'ma' ||
		i18n.language === 'te' ||
		i18n.language === 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const { srcmId } = useAuth();
	const { onFilterChange } = useApplicationsList({});
	const { selectedRole } = useUserSession();
	const [regions, setRegions] = useState<OptionData>();
	const [zones, setZones] = useState<Array<string> | undefined>();
	const [nameValue, setNameValue] = useState<string | undefined>();
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>();
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');
	const [proposerNameValue, setProposerNameValue] = useState<
		string | undefined
	>();
	const [proposerSrcmIdError, setProposerSrcmIdError] = useState('');
	const [proposerSrcmId, setProposerSrcmId] = useState<string | undefined>();
	const isApprover = selectedRole === UserRole.APPROVER;
	const isZC = selectedRole === UserRole.ZC;

	useEffect(() => {
		setZones(filterValue.zoneNames ?? []);
		setRegions(
			filterValue.regionId !== undefined
				? { value: filterValue.regionId, label: undefined }
				: undefined
		);
		setNameValue(filterValue.searchByApplicantName ?? undefined);
		setApplicantSrcmId(filterValue.applicantSrcmId ?? undefined);
		setProposerNameValue(filterValue.searchByProposerName ?? undefined);
		setProposerSrcmId(filterValue.searchByProposerSrcmId ?? undefined);
	}, [filterValue]);

	const handleZoneChange = useCallback((value: Array<{ value: string }>) => {
		setZones(value.map((z) => z.value));
	}, []);

	const handleRegionsChange = useCallback((value: OptionData) => {
		setZones([]);
		setRegions(value);
	}, []);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const onProposerNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setProposerNameValue(e.target.value);
		},
		[]
	);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const onProposerSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setProposerSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setProposerSrcmIdError('invalidAbhyasiId');
			} else {
				setProposerSrcmIdError('');
			}
		},
		[]
	);

	const handleResetFilters = useCallback(() => {
		setZones([]);
		setRegions(undefined);
		onApplicationFilterChange({});
		onFilterChange({
			status: [
				ApplicationStatus.FUNCTIONARY_ASSIGNED,
				ApplicationStatus.FUNCTIONARY_SUBMITTED,
				ApplicationStatus.WAITING_FOR_CARE_TEAM,
				ApplicationStatus.APPROVED,
				ApplicationStatus.REJECTED,
				ApplicationStatus.USER_SUBMITTED_APPLICATION,
			],
		});
		onClose();
	}, [onClose, onFilterChange, onApplicationFilterChange]);

	const handleFilterButtonClick = useCallback(() => {
		onApplicationFilterChange({
			regionId: regions?.value,
			zoneNames: zones,
			applicantSrcmId: applicantSrcmIdValue,
			searchByApplicantName: nameValue,
			searchByProposerName: proposerNameValue,
			searchByProposerSrcmId: proposerSrcmId,
		});

		const defaultFilters: ApplicationsFilter = {
			regionIds: regions?.value ? [regions.value] : [],
			zoneNames: zones,
			applicantSrcmId: applicantSrcmIdValue,
			searchByApplicantName: nameValue,
			searchByProposerName: proposerNameValue,
			searchByProposerSrcmId: proposerSrcmId,
			status: [
				ApplicationStatus.FUNCTIONARY_ASSIGNED,
				ApplicationStatus.FUNCTIONARY_SUBMITTED,
				ApplicationStatus.WAITING_FOR_CARE_TEAM,
				ApplicationStatus.APPROVED,
				ApplicationStatus.REJECTED,
				ApplicationStatus.USER_SUBMITTED_APPLICATION,
			],
		};
		onFilterChange(defaultFilters);
		onClose();
	}, [
		onClose,
		onFilterChange,
		onApplicationFilterChange,
		zones,
		regions?.value,
		applicantSrcmIdValue,
		nameValue,
		proposerNameValue,
		proposerSrcmId,
	]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterDashboard')}
			</div>

			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				{!isApprover && !isZC && (
					<>
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="block text-xs text-gray-650">
									{t('filterByRegion')}
								</Text>
								<RegionsDropdown
									functionarySrcmId={srcmId}
									value={regions}
									onChange={handleRegionsChange}
									getRegionChange={true}
								/>
							</div>
						</div>
						<div className="w-full sm:w-[calc(50%-0.5rem)]">
							<div className="mb-[16px] flex flex-col gap-2">
								<Text className="block text-xs text-gray-650">
									{t('filterByZone')}
								</Text>
								<ZoneDropdown
									functionarySrcmId={srcmId}
									value={zones}
									onChange={handleZoneChange}
									regionId={regions?.value}
									key={regions?.value}
								/>
							</div>
						</div>
					</>
				)}

				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByApplicantName')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={nameValue}
							allowClear
							onChange={onApplicantNameChange}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByApplicantSRCMId')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={applicantSrcmIdValue}
							allowClear
							onChange={onApplicantSRCMIdChange}
						/>
						{applicantSrcmIdError && (
							<Text className="text-xs text-red-500">
								{t(applicantSrcmIdError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByProposerName')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={proposerNameValue}
							allowClear
							onChange={onProposerNameChange}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByProposerSRCMId')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={proposerSrcmId}
							allowClear
							onChange={onProposerSRCMIdChange}
						/>
						{proposerSrcmIdError && (
							<Text className="text-xs text-red-500">
								{t(proposerSrcmIdError)}
							</Text>
						)}
					</div>
				</div>
			</div>
			<div className="flex justify-center">
				<Button
					className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}

						<UndoOutlined className="ml-2" />
					</span>
				</Button>
			</div>
			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-center gap-2'
				} mt-4 ant-modal-footer`}
			>
				<Button
					className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
